/**
 * Operations portal RBAC — five roles per the SimCollect PRD.
 * `super_admin` implicitly has access to everything; it never needs to be
 * listed explicitly in an `allowedRoles` array (see `roleCanAccess` below).
 */
export const ROLES = {
  SUPER_ADMIN: "super_admin",
  EXECUTIVE_MANAGER: "commercial_executive",
  CALL_CENTER_MANAGER: "call_center_manager",
  CALL_CENTER_AGENT: "call_center_agent",
  LEGAL_OFFICER: "legal_officer",
};

export const ALL_ROLES = Object.values(ROLES);

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.EXECUTIVE_MANAGER]: "Commercial Executive",
  [ROLES.CALL_CENTER_MANAGER]: "Call Centre Manager",
  [ROLES.CALL_CENTER_AGENT]: "Call Centre Agent",
  [ROLES.LEGAL_OFFICER]: "Legal Officer",
};

export const ROLE_DESCRIPTIONS = {
  [ROLES.SUPER_ADMIN]: "Full platform access — institutions, team, all cases and data",
  [ROLES.EXECUTIVE_MANAGER]: "Org-wide oversight, institution assignment, and commercial KPIs",
  [ROLES.CALL_CENTER_MANAGER]: "Case allocation, SLA monitoring, settlement approvals",
  [ROLES.CALL_CENTER_AGENT]: "Own assigned cases — calls, notes, payment links",
  [ROLES.LEGAL_OFFICER]: "Escalated / legal-stage cases only",
};

/**
 * Normalize API role labels (e.g. "Call Center Agent") to slug keys used in ROLES.
 */
export function normalizeRole(role) {
  if (!role) return null;
  const normalized = String(role)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (normalized === "executive_manager") return ROLES.EXECUTIVE_MANAGER;
  return normalized;
}

/**
 * Returns true when `role` is allowed to access something gated by
 * `allowedRoles`. `super_admin` always passes. `undefined`/empty
 * `allowedRoles` means "no restriction — everyone signed in can access it".
 */
export function roleCanAccess(role, allowedRoles) {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const normalized = normalizeRole(role);
  if (normalized === ROLES.SUPER_ADMIN || normalized === ROLES.EXECUTIVE_MANAGER) return true;
  return allowedRoles.some((allowed) => normalizeRole(allowed) === normalized);
}

export function getRoleLabel(role) {
  const key = normalizeRole(role);
  return ROLE_LABELS[key] ?? String(role ?? "Ops user").replace(/_/g, " ");
}
